import { isPatternMode, iterateUntilEscapePatternMode } from './fractalIteration';

export const EXPLORER_PALETTE = {
  style: 'lagoon', gScale: 7, aFixed: 255, useMu: true
};
export const PALETTE_SIZE = 512;
export const PALETTE_ROWS = 4;

// Each cube root of unity owns a dedicated periodic row in the 512x4 palette
// texture so root basins never bleed into one another's hues at higher
// iteration counts. Within each row, chiaroscuro depth darkens toward sumi-ink
// along the Julia boundary while porcelain-washi crests trace contour seams.
const ROOT_BASES = [
  [223, 109, 83],  // r0: Persimmon Coral
  [60, 163, 133],  // r1: Celadon Jade
  [75, 108, 183],  // r2: Indigo Sea
  [216, 164, 68]   // r3: Imperial Ochre (fourth root basin)
];
const HIGHLIGHT = [242, 245, 239];
const SHADOW = [10, 14, 16];

function smoothstep(edge0, edge1, x) {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

export const paletteTable = new Uint8Array(PALETTE_SIZE * PALETTE_ROWS * 4);
for (let row = 0; row < PALETTE_ROWS; row++) {
  const base = ROOT_BASES[row < ROOT_BASES.length ? row : 0];
  const rowOffset = row * PALETTE_SIZE * 4;
  for (let i = 0; i < PALETTE_SIZE; i++) {
    const phase = i / PALETTE_SIZE;
    const u = (phase - 0.12 + 1.0) % 1.0;
    const theta = 2 * Math.PI * u;
    const boundaryEnv = Math.sin(Math.PI * u);
    const crestGate = smoothstep(0.14, 0.22, u) * smoothstep(0.0, 0.08, 1.0 - u);
    const troughGate = smoothstep(0.23, 0.31, u) * smoothstep(0.0, 0.10, 1.0 - u);
    const wave = 0.5 + 0.5 * Math.sin(6 * theta - 1.05);
    const seamTrough = Math.pow(1.0 - wave, 3.0) * 0.42 * troughGate;
    const depth = (1.0 - 0.36 * Math.pow(boundaryEnv, 2.0)) * (1.0 - seamTrough);
    const crest = Math.pow(wave, 3.2) * 0.38 * depth * crestGate;
    for (let channel = 0; channel < 3; channel++) {
      const value = base[channel] * (0.22 + 0.78 * depth) * (1 - crest) +
        HIGHLIGHT[channel] * crest +
        SHADOW[channel] * (1 - depth) * 0.68;
      paletteTable[rowOffset + i * 4 + channel] = Math.round(Math.max(0, Math.min(255, value)));
    }
    paletteTable[rowOffset + i * 4 + 3] = 255;
  }
}

// Retained for callers of the original blue/yellow palette.
export function palettePosition(value, gScale = 7, repeat = false) {
  const position = value * gScale / 255;
  return repeat ? 0.5 - 0.5 * Math.cos(Math.PI * position) : Math.max(0, Math.min(1, position));
}

export function palettePhase(value, gScale = 7) {
  // Compress high escape counts gently, retaining separation without densely
  // packed stripes at the boundary. No cutoff: detail keeps receiving color.
  return (Math.sqrt(Math.max(0, value) + 1) - 1) * gScale / 55;
}

export function samplePalette(value, params, out, shade = 1, root = 0) {
  if (params.style === 'lagoon') {
    const row = !params.rootSymmetric && root ? (root & 3) : 0;
    const rowBase = row * (PALETTE_SIZE << 2);
    const phase = palettePhase(value, params.gScale) + (params.phaseOffset ?? 0);
    const position = (phase - Math.floor(phase)) * PALETTE_SIZE;
    const index = Math.floor(position);
    const fraction = position - index;
    const base0 = rowBase + (index << 2);
    const base1 = rowBase + (((index + 1) & (PALETTE_SIZE - 1)) << 2);
    const r0 = paletteTable[base0];
    const g0 = paletteTable[base0 + 1];
    const b0 = paletteTable[base0 + 2];
    out[0] = Math.round(r0 + (paletteTable[base1] - r0) * fraction);
    out[1] = Math.round(g0 + (paletteTable[base1 + 1] - g0) * fraction);
    out[2] = Math.round(b0 + (paletteTable[base1 + 2] - b0) * fraction);
  } else {
    const t = palettePosition(value, params.gScale, params.repeat);
    out[0] = out[1] = Math.round(255 * t);
    out[2] = Math.round(255 * (1 - t));
  }
  if (shade !== 1 && Number.isFinite(shade)) {
    out[0] = Math.round(Math.max(0, Math.min(255, out[0] * shade)));
    out[1] = Math.round(Math.max(0, Math.min(255, out[1] * shade)));
    out[2] = Math.round(Math.max(0, Math.min(255, out[2] * shade)));
  }
  out[3] = params.aFixed === 255 ? 255
    : Math.max(0, Math.min(255, Math.round(params.aFixed ?? value * (params.aScale ?? 15))));
  return out;
}

const stripeScratch = { iterations: 0, escaped: false, mu: 0, shade: 1 };

export function paletteColor(input, params) {
  if (isPatternMode(params?.mode) && params.useMu !== false) {
    if (input.stripe === true) {
      return samplePalette(input.mu, params, [0, 0, 0, 0], input.shade ?? 1, input.root ?? 0);
    }
    if (Number.isFinite(input.x0) && Number.isFinite(input.y0)) {
      iterateUntilEscapePatternMode(input.x0, input.y0, stripeScratch, params.mode, undefined, true);
      if (!stripeScratch.escaped) return [0, 0, 0, 0];
      return samplePalette(stripeScratch.mu, params, [0, 0, 0, 0], stripeScratch.shade);
    }
  }
  const { mu, numIterations, shade = 1, root = 0 } = input;
  return samplePalette(params.useMu === false ? numIterations : mu, params, [0, 0, 0, 0], shade, root);
}
